<template>
  <v-dialog :value="value" persistent scrollable max-width="900" @input="onDialogInput">
    <v-card class="ds-dialog" color="bg1">
      <div class="ds-dialog__header">
        <div class="ds-dialog__header-icon">
          <v-icon color="primary" size="20">{{ headerIcon }}</v-icon>
        </div>
        <div class="ds-dialog__header-text">
          <div class="ds-dialog__title">{{ dialogTitle }}</div>
          <div class="ds-dialog__subtitle">{{ dialogSubtitle }}</div>
        </div>
        <v-btn icon small :disabled="isBusy" @click="close">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="ds-dialog__body">
        <!-- 1. Audience ------------------------------------------------------------------ -->
        <div class="ds-section-header">
          <div class="ds-field-label">Audience</div>
        </div>
        <p class="publish-dialog__help">
          Pick the companies that should receive these release notes. Suspended or expired companies
          stay selectable — the server decides who is actually eligible.
        </p>
        <!-- `attach` keeps the picker's menu inside the dialog's stacking context. -->
        <company-picker
          attach
          :value="companyIds"
          :disabled="isBusy"
          @input="onCompaniesChanged"
          @update:companies="selectedCompanies = $event" />
        <v-alert
          v-if="audienceError"
          dense
          outlined
          type="error"
          class="publish-dialog__audience-error">
          {{ audienceError }}
        </v-alert>
        <v-alert
          v-else-if="flaggedCompanyNames.length"
          dense
          outlined
          type="warning"
          class="publish-dialog__audience-error">
          {{ flaggedCompanyNames.join(', ') }}
          {{ flaggedCompanyNames.length === 1 ? 'is' : 'are' }} suspended or expired. They stay
          selectable — run a preview to see whether the server resolves any recipients for them.
        </v-alert>

        <v-divider class="my-4" />

        <!-- 2. Recipient policy ---------------------------------------------------------- -->
        <div class="ds-field-label">Who gets the email</div>
        <v-radio-group
          v-model="recipientPolicy"
          :disabled="isBusy"
          dense
          hide-details
          class="mt-1 mb-2">
          <v-radio
            v-for="option in recipientPolicyOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value" />
        </v-radio-group>

        <v-divider class="my-4" />

        <!-- 3. Email toggle -------------------------------------------------------------- -->
        <v-checkbox
          v-model="sendEmail"
          :disabled="isBusy"
          dense
          hide-details
          label="Send email to recipients" />
        <p class="publish-dialog__help mt-1">
          With this off, publishing only makes the release page live — nobody is emailed. You can
          still notify these companies later with Re-notify.
        </p>

        <v-checkbox
          v-model="notifyInApp"
          class="mt-3"
          :disabled="isBusy"
          dense
          hide-details
          label="Notify users in the app" />
        <p class="publish-dialog__help mt-1">
          The same people the email is addressed to also get an in-app notification — including
          anyone whose email is suppressed.
        </p>

        <v-divider class="my-4" />

        <!-- 4. Reminder ------------------------------------------------------------------ -->
        <v-checkbox
          v-model="reminderEnabled"
          :disabled="isBusy"
          dense
          hide-details
          label="Remind release owners to deploy" />
        <p class="publish-dialog__help mt-1">
          Schedules a one-off reminder so somebody chases the actual app deployment. A reminder can
          only be armed here, while publishing.
        </p>

        <v-expansion-panels v-if="reminderEnabled" v-model="advancedPanel" flat class="mt-2">
          <v-expansion-panel>
            <v-expansion-panel-header class="publish-dialog__advanced-header">
              Advanced reminder options
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-text-field
                v-model="reminderDelayDays"
                type="number"
                outlined
                dense
                min="1"
                max="90"
                label="Remind after (days)"
                hint="Leave empty to use the configured default."
                persistent-hint
                :disabled="isBusy"
                :error-messages="delayDaysError"
                class="publish-dialog__delay" />

              <v-combobox
                v-model="reminderEmails"
                :items="[]"
                :disabled="isBusy"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                hide-no-data
                label="Reminder recipients (override)"
                hint="Leave empty to use the recipients configured in Settings. Press Enter after each address."
                persistent-hint
                :error-messages="reminderEmailsError"
                class="mt-4" />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-divider class="my-4" />

        <!-- 5. Preview ------------------------------------------------------------------- -->
        <div class="publish-dialog__preview-bar">
          <div class="ds-field-label mb-0">Preview</div>
          <v-spacer />
          <v-btn
            small
            outlined
            color="primary"
            :loading="isPreviewLoading"
            :disabled="!canPreview"
            @click="runPreview">
            <v-icon left small>mdi-eye-outline</v-icon>
            Preview
          </v-btn>
        </div>
        <publish-preview-table :preview="activePreview" :loading="isPreviewLoading" />
      </div>

      <div class="ds-dialog__footer">
        <div class="ds-dialog__hint">{{ footerHint }}</div>
        <v-spacer />
        <v-btn text small :disabled="isBusy" @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          small
          :loading="isPublishing"
          :disabled="!canPublish"
          @click="submit">
          <v-icon left small>{{ headerIcon }}</v-icon>
          {{ confirmLabel }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  import { warn } from '@/utils/debugLogger';
  import formRules from '@Share/config/form-rules';
  import CompanyPicker from '@ReleaseNotes/components/CompanyPicker.vue';
  import PublishPreviewTable from '@ReleaseNotes/components/PublishPreviewTable.vue';
  import {
    RELEASE_ERROR_MESSAGES,
    ReleaseRecipientPolicy,
    ReleaseRecipientPolicyLabels,
    ReleaseStatus,
    releaseErrorCode,
    releaseErrorMessage,
  } from '@ReleaseNotes/config/enums';

  const MIN_DELAY_DAYS = 1;
  const MAX_DELAY_DAYS = 90;

  /**
   * PublishDialog — a scrollable multi-section dialog (NOT a wizard, per plan §6.4) that drives
   * `POST {id}/publish/preview` and `POST {id}/publish`.
   *
   * Preview gating: the confirm button stays disabled until a preview has been run against the
   * EXACT current form state. `formSnapshot` is a deterministic serialisation of the request body;
   * `previewSnapshot` records the snapshot the last successful preview was run for. Any field
   * change makes the two diverge, which both disables the button and clears the stale preview so
   * the user is never looking at counts for a form they have since edited.
   *
   * Error handling: `PUBLISH` deliberately does NOT toast a `400 RELEASE_AUDIENCE_EMPTY`
   * (addendum §C) — that case is actionable right where the audience is chosen, so it renders as
   * an inline alert under the company picker and the dialog stays open. Everything else is
   * already toasted inside the Vuex action.
   *
   * There is no approval/review step in Phase 1 (plan §9): the copy must not imply one.
   */
  export default {
    name: 'PublishDialog',

    components: { CompanyPicker, PublishPreviewTable },

    props: {
      value: { type: Boolean, default: false },
      release: { type: Object, default: null },
      releaseId: { type: [String, Number], default: null },
    },

    data() {
      return {
        formRules,
        companyIds: [],
        selectedCompanies: [],
        recipientPolicy: ReleaseRecipientPolicy.CompanyOwners,
        sendEmail: true,
        notifyInApp: true,
        reminderEnabled: false,
        reminderDelayDays: '',
        reminderEmails: [],
        advancedPanel: undefined,
        // Serialised form state the last successful preview was run against.
        previewSnapshot: null,
        // Preview payload captured locally, so a preview left in global Vuex state by an earlier
        // dialog session can never be mistaken for this session's.
        localPreview: null,
        audienceError: '',
        isPublishing: false,
      };
    },

    computed: {
      isRepublish() {
        return !!this.release && this.release.status === ReleaseStatus.Published;
      },

      headerIcon() {
        return this.isRepublish ? 'mdi-email-sync-outline' : 'mdi-bullhorn-outline';
      },

      dialogTitle() {
        return this.isRepublish ? 'Re-notify companies' : 'Publish release notes';
      },

      dialogSubtitle() {
        if (this.isRepublish) {
          return 'These notes are already live. Re-notifying sends them again to the companies you pick below.';
        }
        return 'Makes the release page live for the chosen companies and, unless you turn it off, emails their recipients.';
      },

      confirmLabel() {
        return this.isRepublish ? 'Re-notify' : 'Publish';
      },

      recipientPolicyOptions() {
        return Object.keys(ReleaseRecipientPolicyLabels).map((key) => ({
          value: Number(key),
          label: ReleaseRecipientPolicyLabels[key],
        }));
      },

      settings() {
        return this.$store.getters['RELEASE_NOTES/GET_SETTINGS'];
      },

      /**
       * Names of picked companies the picker flagged as suspended/expired. Purely advisory —
       * the server enforces eligibility, so these companies remain selectable (plan §6.4).
       * Probed defensively because the companies DTO is unverified.
       */
      flaggedCompanyNames() {
        return (this.selectedCompanies || [])
          .filter((company) => {
            if (!company) return false;
            if (company.isSuspended === true || company.suspended === true) return true;
            if (company.isExpired === true || company.expired === true) return true;
            if (typeof company.status !== 'string') return false;
            const status = company.status.toLowerCase();
            return status.includes('suspend') || status.includes('expire');
          })
          .map((company) => company.name || '(unnamed)');
      },

      isPreviewLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_PUBLISH_PREVIEW_LOADING'];
      },

      activePreview() {
        return this.isPreviewValid ? this.localPreview : null;
      },

      isBusy() {
        return this.isPublishing || this.isPreviewLoading;
      },

      /** Deterministic serialisation of the request body — the preview-gating key. */
      formSnapshot() {
        return JSON.stringify(this.buildBody());
      },

      isPreviewValid() {
        return !!this.previewSnapshot && this.previewSnapshot === this.formSnapshot;
      },

      delayDaysError() {
        if (!this.reminderEnabled) return '';
        const raw = this.reminderDelayDays;
        if (raw === '' || raw === null || raw === undefined) return '';
        const parsed = Number(raw);
        if (!Number.isInteger(parsed)) return 'Enter a whole number of days.';
        if (parsed < MIN_DELAY_DAYS || parsed > MAX_DELAY_DAYS) {
          return `Must be between ${MIN_DELAY_DAYS} and ${MAX_DELAY_DAYS} days.`;
        }
        return '';
      },

      invalidReminderEmails() {
        return (this.reminderEmails || []).filter(
          (entry) => formRules.validEmail(String(entry || '').trim()) !== true,
        );
      },

      reminderEmailsError() {
        if (!this.reminderEnabled || !this.invalidReminderEmails.length) return '';
        return `Not a valid email address: ${this.invalidReminderEmails.join(', ')}`;
      },

      isFormValid() {
        if (!this.companyIds.length) return false;
        if (!this.reminderEnabled) return true;
        return !this.delayDaysError && !this.invalidReminderEmails.length;
      },

      canPreview() {
        return !!this.releaseId && this.isFormValid && !this.isBusy;
      },

      canPublish() {
        return !!this.releaseId && this.isFormValid && this.isPreviewValid && !this.isBusy;
      },

      footerHint() {
        if (!this.companyIds.length) return 'Pick at least one company.';
        if (!this.isFormValid) return 'Fix the highlighted reminder fields to continue.';
        if (!this.isPreviewValid) return 'Run a preview to continue.';
        return this.sendEmail
          ? 'Emails are queued in the background once you confirm.'
          : 'No emails will be sent — the release page just goes live.';
      },
    },

    watch: {
      value(opened) {
        if (opened) this.resetForm();
      },

      /**
       * Any field change invalidates the previous preview: drop it so the table falls back to its
       * "Run a preview" placeholder rather than showing counts for a form that has moved on.
       */
      formSnapshot(snapshot) {
        if (this.previewSnapshot && this.previewSnapshot !== snapshot) {
          this.previewSnapshot = null;
          this.localPreview = null;
        }
      },
    },

    methods: {
      onDialogInput(opened) {
        // `persistent` means this only fires for programmatic closes, but keep the contract honest.
        this.$emit('input', opened);
      },

      close() {
        if (this.isBusy) return;
        this.$emit('input', false);
      },

      onCompaniesChanged(ids) {
        this.companyIds = ids || [];
        // The audience error is about the previous selection; a new pick deserves a clean slate.
        this.audienceError = '';
      },

      /** Rebuilds every field from props + settings each time the dialog opens. */
      resetForm() {
        this.companyIds = [];
        this.selectedCompanies = [];
        this.recipientPolicy = ReleaseRecipientPolicy.CompanyOwners;
        this.sendEmail = true;
        this.notifyInApp = true;
        this.reminderEnabled = false;
        this.reminderDelayDays = '';
        this.reminderEmails = [];
        this.advancedPanel = undefined;
        this.previewSnapshot = null;
        this.localPreview = null;
        this.audienceError = '';
        this.isPublishing = false;

        if (this.settings) {
          this.applySettingsDefaults(this.settings);
          return;
        }
        this.$store
          .dispatch('RELEASE_NOTES/FETCH_SETTINGS')
          .then((settings) => {
            // Only apply if the dialog is still open and untouched by the user.
            if (this.value && settings && !this.companyIds.length) {
              this.applySettingsDefaults(settings);
            }
          })
          .catch((err) => {
            warn('[PublishDialog] settings unavailable, using built-in defaults', err);
          });
      },

      /**
       * Seeds defaults from the feature settings DTO. Every field is probed defensively — the
       * settings shape is unverified, and a wrong default is worse than the built-in one.
       * @param {object} settings
       */
      applySettingsDefaults(settings) {
        if (!settings) return;

        const policy = [settings.defaultRecipientPolicy, settings.recipientPolicy].find((value) =>
          Number.isFinite(Number(value)),
        );
        if (policy !== undefined && Number(policy) > 0) this.recipientPolicy = Number(policy);

        const reminderDefault = [settings.reminderEnabledByDefault, settings.reminderEnabled].find(
          (value) => typeof value === 'boolean',
        );
        if (reminderDefault !== undefined) this.reminderEnabled = reminderDefault;

        const delay = [settings.reminderDelayDays, settings.delayDays].find((value) =>
          Number.isFinite(Number(value)),
        );
        if (delay !== undefined && Number(delay) > 0)
          this.reminderDelayDays = String(Number(delay));
      },

      /**
       * Request body shared by preview and publish.
       * `reminder` is omitted entirely when the reminder is off — the server treats an absent
       * object as "no reminder", and sending `{ enabled: false }` is not the documented contract.
       */
      buildBody() {
        const body = {
          companyIds: (this.companyIds || []).slice(),
          recipientPolicy: this.recipientPolicy,
          sendEmail: this.sendEmail,
          notifyInApp: this.notifyInApp,
        };

        if (this.reminderEnabled) {
          const reminder = { enabled: true };
          const delay = Number(this.reminderDelayDays);
          if (this.reminderDelayDays !== '' && Number.isInteger(delay)) reminder.delayDays = delay;

          const emails = (this.reminderEmails || [])
            .map((entry) => String(entry || '').trim())
            .filter((entry) => entry.length > 0);
          if (emails.length) reminder.recipientEmails = emails;

          body.reminder = reminder;
        }

        return body;
      },

      async runPreview() {
        if (!this.canPreview) return;
        this.audienceError = '';
        const snapshot = this.formSnapshot;

        try {
          const preview = await this.$store.dispatch('RELEASE_NOTES/FETCH_PUBLISH_PREVIEW', {
            id: this.releaseId,
            data: this.buildBody(),
          });
          // The user may have edited a field while the request was in flight; in that case the
          // result is already stale and must not re-enable the confirm button.
          if (snapshot !== this.formSnapshot) return;
          this.localPreview = preview || null;
          this.previewSnapshot = snapshot;
        } catch (err) {
          // FETCH_PUBLISH_PREVIEW already toasted; just keep the gate closed.
          this.previewSnapshot = null;
          this.localPreview = null;
          warn('[PublishDialog] publish preview failed', err);
        }
      },

      async submit() {
        if (!this.canPublish) return;
        this.audienceError = '';
        this.isPublishing = true;

        try {
          await this.$store.dispatch('RELEASE_NOTES/PUBLISH', {
            id: this.releaseId,
            data: this.buildBody(),
          });
          this.$emit('input', false);
          this.$dialog.notify.success('Published — emails are being sent', {
            position: 'top-right',
            timeout: 3000,
          });
          this.$emit('published');
        } catch (err) {
          if (releaseErrorCode(err) === 'RELEASE_AUDIENCE_EMPTY') {
            // Caller-handled (addendum §C): show it inline next to the picker, keep the dialog open.
            this.audienceError = releaseErrorMessage(
              err,
              RELEASE_ERROR_MESSAGES.RELEASE_AUDIENCE_EMPTY,
            );
          } else {
            warn('[PublishDialog] publish failed', err);
          }
        } finally {
          this.isPublishing = false;
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .publish-dialog__help {
    font-size: 0.75rem;
    opacity: 0.65;
    margin-bottom: 8px;
  }

  .publish-dialog__audience-error {
    margin-top: 8px;
    margin-bottom: 0;
    font-size: 0.8125rem;
  }

  .publish-dialog__preview-bar {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  }

  .publish-dialog__advanced-header {
    font-size: 0.8125rem;
    min-height: 40px;
  }

  .publish-dialog__delay {
    max-width: 220px;
  }
</style>
